/**
 * Security Trust Panel — artifact trust cards, SQA report cards, composite trust
 * Phase 4.8-B — WQ-200
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getTrustDashboard,
  getPlatformReportCard,
  getNCRSummary,
  type TrustDashboardItem,
  type PlatformReportCard,
  type NCRSummary,
} from '../../services/securityAssessmentService';
import { fmt, fmtPct } from '../../utils/formatNumber';

const RISK_COLORS: Record<string, string> = {
  low: '#22c55e',
  medium: '#f59e0b',
  high: '#f97316',
  critical: '#ef4444',
};

type SubTab = 'trust' | 'sqa' | 'composite';

function GaugeChart({ score, label, size = 120 }: { score: number; label: string; size?: number }) {
  const radius = (size - 20) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = Math.PI * radius;
  const progress = (score / 100) * circumference;
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444';

  return (
    <div style={{ textAlign: 'center' }}>
      <svg width={size} height={size / 2 + 20} viewBox={`0 0 ${size} ${size / 2 + 20}`}>
        <path
          d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
          fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={10} strokeLinecap="round"
        />
        <path
          d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
          fill="none" stroke={color} strokeWidth={10} strokeLinecap="round"
          strokeDasharray={`${progress} ${circumference}`}
        />
        <text x={cx} y={cy - 8} textAnchor="middle" fill={color} fontSize={22} fontWeight={700}>
          {fmt(score, 0)}
        </text>
      </svg>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: -4 }}>{label}</div>
    </div>
  );
}

const SecurityTrustPanel: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<SubTab>('trust');
  const [trustItems, setTrustItems] = useState<TrustDashboardItem[]>([]);
  const [reportCard, setReportCard] = useState<PlatformReportCard | null>(null);
  const [ncrSummary, setNCRSummary] = useState<NCRSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [trust, report, ncrs] = await Promise.all([
        getTrustDashboard(authFetch),
        getPlatformReportCard(authFetch),
        getNCRSummary(authFetch),
      ]);
      setTrustItems(Array.isArray(trust) ? trust : []);
      setReportCard(report);
      setNCRSummary(ncrs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load security data');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) fetchData();
  }, [authLoading]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.1)', borderTop: '3px solid #00d4ff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass" style={{ padding: 20, textAlign: 'center' }}>
        <p style={{ color: '#ef4444', marginBottom: 12 }}>{error}</p>
        <button onClick={fetchData} className="glass" style={{ padding: '6px 16px', cursor: 'pointer', color: '#00d4ff', background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)', borderRadius: 6 }}>
          Retry
        </button>
      </div>
    );
  }

  const subTabs: { key: SubTab; label: string }[] = [
    { key: 'trust', label: 'Artifact Trust' },
    { key: 'sqa', label: 'SQA Report' },
    { key: 'composite', label: 'Composite' },
  ];

  const riskDistribution = trustItems.reduce<Record<string, number>>((acc, item) => {
    acc[item.overall_risk_level] = (acc[item.overall_risk_level] || 0) + 1;
    return acc;
  }, {});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Sub-tab navigation */}
      <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: 4 }}>
        {subTabs.map(tab => (
          <button key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              flex: 1, padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
              background: activeTab === tab.key ? 'rgba(0,212,255,0.15)' : 'transparent',
              color: activeTab === tab.key ? '#00d4ff' : 'rgba(255,255,255,0.5)',
              transition: 'all 0.2s',
            }}
          >{tab.label}</button>
        ))}
      </div>

      {/* Artifact Trust Tab */}
      {activeTab === 'trust' && (
        <>
          {/* Risk Distribution */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
            {Object.entries(riskDistribution).map(([level, count]) => (
              <div key={level} className="glass" style={{ padding: 16, textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: RISK_COLORS[level] || '#6b7280' }}>{count}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4, textTransform: 'capitalize' }}>{level} Risk</div>
              </div>
            ))}
          </div>

          {/* Trust Card Table */}
          <div className="glass" style={{ padding: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'rgba(255,255,255,0.8)' }}>
              Artifact Trust Scores
            </h3>
            {trustItems.length === 0 ? (
              <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 20 }}>No assessments yet</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                      <th style={{ textAlign: 'left', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Artifact</th>
                      <th style={{ textAlign: 'center', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Trust Score</th>
                      <th style={{ textAlign: 'center', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Risk Level</th>
                      <th style={{ textAlign: 'center', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Threshold</th>
                      <th style={{ textAlign: 'right', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Assessed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trustItems.map(item => (
                      <tr key={item.artifact_id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>{item.artifact_id.slice(0, 8)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <span style={{ fontWeight: 600, color: item.artifact_trust_score >= 70 ? '#22c55e' : item.artifact_trust_score >= 40 ? '#f59e0b' : '#ef4444' }}>
                            {fmtPct(item.artifact_trust_score, 0)}
                          </span>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <span style={{
                            padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                            background: `${RISK_COLORS[item.overall_risk_level] || '#6b7280'}20`,
                            color: RISK_COLORS[item.overall_risk_level] || '#6b7280',
                          }}>{item.overall_risk_level}</span>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          {item.threshold_met ? (
                            <span style={{ color: '#22c55e', fontWeight: 600, fontSize: 13 }}>Pass</span>
                          ) : (
                            <span style={{ color: '#ef4444', fontWeight: 600, fontSize: 13 }}>Fail</span>
                          )}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                          {new Date(item.assessed_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* SQA Report Tab */}
      {activeTab === 'sqa' && reportCard && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            <div className="glass" style={{ padding: 16, display: 'flex', justifyContent: 'center' }}>
              <GaugeChart score={reportCard.overall_confidence * 100} label="Platform Confidence" />
            </div>
            <div className="glass" style={{ padding: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#00d4ff' }}>{reportCard.component_count}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Components</div>
            </div>
            {ncrSummary && (
              <>
                <div className="glass" style={{ padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#ef4444' }}>{ncrSummary.by_severity?.critical ?? 0}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Critical NCRs</div>
                </div>
                <div className="glass" style={{ padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#f59e0b' }}>{ncrSummary.total}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Total NCRs</div>
                </div>
              </>
            )}
          </div>

          {/* Component Cards */}
          <div className="glass" style={{ padding: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'rgba(255,255,255,0.8)' }}>
              Component Report Cards
            </h3>
            {reportCard.components.length === 0 ? (
              <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 20 }}>No SQA reports available</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                {reportCard.components.map(comp => (
                  <div key={comp.component_id} className="glass" style={{ padding: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{comp.component_name}</span>
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                        background: 'rgba(0,212,255,0.1)', color: '#00d4ff',
                      }}>{comp.integrity_level}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.05)', borderRadius: 3 }}>
                        <div style={{
                          height: '100%', borderRadius: 3, transition: 'width 0.5s ease',
                          width: `${comp.confidence_score * 100}%`,
                          background: comp.confidence_score >= 0.85 ? '#22c55e' : comp.confidence_score >= 0.7 ? '#f59e0b' : '#ef4444',
                        }} />
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 600, minWidth: 40, textAlign: 'right' }}>
                        {fmtPct(comp.confidence_score, 0)}
                      </span>
                    </div>
                    {comp.dimensions && Object.entries(comp.dimensions).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {Object.entries(comp.dimensions).map(([dim, val]) => (
                          <span key={dim} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)' }}>
                            {dim}: {typeof val === 'number' ? (val * 100).toFixed(0) + '%' : String(val)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Composite Tab */}
      {activeTab === 'composite' && (
        <div className="glass" style={{ padding: 20, textAlign: 'center' }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'rgba(255,255,255,0.8)' }}>
            Composite Trust Score (60% Security / 40% SQA)
          </h3>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 32, flexWrap: 'wrap', marginBottom: 24 }}>
            {trustItems.length > 0 && (
              <GaugeChart
                score={(trustItems.reduce((s, i) => s + i.artifact_trust_score, 0) / trustItems.length) * 100}
                label="Avg Security Trust" size={140}
              />
            )}
            {reportCard && (
              <GaugeChart score={reportCard.overall_confidence * 100} label="SQA Confidence" size={140} />
            )}
            {trustItems.length > 0 && reportCard && (
              <GaugeChart
                score={
                  ((trustItems.reduce((s, i) => s + i.artifact_trust_score, 0) / trustItems.length) * 0.6 +
                    reportCard.overall_confidence * 0.4) * 100
                }
                label="Composite Score" size={160}
              />
            )}
          </div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
            {trustItems.length} artifacts assessed | {reportCard?.component_count ?? 0} SQA components tracked
          </p>
        </div>
      )}
    </div>
  );
};

export default SecurityTrustPanel;
